import { HttpClient } from '@adapters/HttpClient';
import type { CourierSettlementLinesFilters } from '../models/settlementLinesFilters';
import { buildSettlementLinesQueryParams, serializeSettlementLinesParams } from '../models/settlementLinesFilters';
import type {
  CourierInvoiceSummaryNested,
  CourierSettlementListItem,
  CourierSettlementsListResponse,
  PaginatorMeta,
} from '../models/types';

const BASE = '/courier-settlements';

/** Respuesta paginada de GET `/courier-settlements/:id/lines` */
export interface CourierSettlementLinesPage {
  data: unknown[];
  meta: PaginatorMeta;
  /** Si es false, `meta` se infirió (API sin bloque meta); no usar `meta.total` como total global. */
  metaFromApi: boolean;
}

function inferLinesPaginatorMeta(dataLen: number, page: number, per_page: number): PaginatorMeta {
  /** Respuesta sin meta pero con más filas que `per_page`: suele ser “todo en un solo payload”. */
  const oversizedSinglePayload = dataLen > per_page;
  const last_page = oversizedSinglePayload ? page : dataLen === 0 || dataLen < per_page ? page : page + 1;
  return {
    total: dataLen,
    per_page: oversizedSinglePayload ? Math.max(dataLen, per_page) : per_page,
    current_page: page,
    last_page,
  };
}

function parseMeta(m: Record<string, unknown> | undefined, fallbackPerPage: number, page: number): PaginatorMeta {
  const raw = m ?? {};
  return {
    total: Number(raw.total ?? 0),
    per_page: Number(raw.per_page ?? (raw as { perPage?: number }).perPage ?? fallbackPerPage),
    current_page: Number(raw.current_page ?? (raw as { currentPage?: number }).currentPage ?? page),
    last_page: Number(raw.last_page ?? (raw as { lastPage?: number }).lastPage ?? 1),
  };
}

function mapInvoiceNested(raw: Record<string, unknown> | undefined | null): CourierInvoiceSummaryNested | null {
  if (!raw || raw.id === undefined) {
    return null;
  }
  return {
    id: Number(raw.id),
    supplier_reference: raw.supplier_reference != null ? String(raw.supplier_reference) : null,
    status: raw.status != null ? String(raw.status) : null,
  };
}

function mapSettlementRow(raw: Record<string, unknown>): CourierSettlementListItem {
  return {
    id: Number(raw.id),
    courier_id: raw.courier_id !== undefined ? Number(raw.courier_id) : undefined,
    courier_invoice_id:
      raw.courier_invoice_id !== undefined && raw.courier_invoice_id !== null ? Number(raw.courier_invoice_id) : null,
    courier_invoice: mapInvoiceNested(raw.courier_invoice as Record<string, unknown> | undefined),
    source_filename: raw.source_filename != null ? String(raw.source_filename) : null,
    import_status: raw.import_status != null ? String(raw.import_status) : null,
    imported_at: raw.imported_at != null ? String(raw.imported_at) : null,
    created_at: raw.created_at != null ? String(raw.created_at) : null,
  };
}

/** Respuesta POST `/courier-settlements/:id/recalculate-pending-lines` */
export interface CourierSettlementRecalculatePendingResponse {
  recalculated: number;
  lines_matched_count: number;
}

export class CourierSettlementsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async list(filters: {
    courier_invoice_id?: number;
    page?: number;
    per_page?: number;
  }): Promise<CourierSettlementsListResponse> {
    const page = filters.page ?? 1;
    const per_page = filters.per_page ?? 25;
    const params: Record<string, number> = { page, per_page };
    if (filters.courier_invoice_id !== undefined) {
      params.courier_invoice_id = filters.courier_invoice_id;
    }

    const raw = await this.httpClient.get<{ data: unknown[]; meta?: Record<string, unknown> }>(BASE, params, {}, true);
    const rows = Array.isArray(raw.data) ? raw.data : [];
    const data = rows.map((item) => mapSettlementRow(item as Record<string, unknown>));
    const meta = parseMeta(raw.meta, per_page, page);
    return { data, meta };
  }

  async detail(id: number): Promise<Record<string, unknown>> {
    const raw = await this.httpClient.get<unknown>(`${BASE}/${id}`, {}, {}, true);
    const r = raw as Record<string, unknown>;
    const inner = r.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>;
    }
    return r;
  }

  /**
   * Líneas con paginación (`page`, `per_page`).
   * Si la API ignora los parámetros y devuelve todo en un único payload, `meta.last_page` será 1.
   */
  async linesPaginated(
    id: number,
    page: number,
    per_page: number,
    filters: CourierSettlementLinesFilters = {},
  ): Promise<CourierSettlementLinesPage> {
    const params = buildSettlementLinesQueryParams(filters, { page, per_page }) as Record<string, unknown>;
    const raw = await this.httpClient.get<unknown>(
      `${BASE}/${id}/lines`,
      params as Record<string, any>,
      {
        paramsSerializer: serializeSettlementLinesParams,
      },
      true,
    );

    if (Array.isArray(raw)) {
      const data = raw;
      return {
        data,
        meta: inferLinesPaginatorMeta(data.length, page, per_page),
        metaFromApi: false,
      };
    }

    const r = raw as Record<string, unknown>;
    const data = Array.isArray(r.data) ? (r.data as unknown[]) : [];

    const metaRaw = r.meta;
    if (metaRaw && typeof metaRaw === 'object' && !Array.isArray(metaRaw)) {
      return {
        data,
        meta: parseMeta(metaRaw as Record<string, unknown>, per_page, page),
        metaFromApi: true,
      };
    }

    return {
      data,
      meta: inferLinesPaginatorMeta(data.length, page, per_page),
      metaFromApi: false,
    };
  }

  /** Export Excel; mismos filtros que el listado (sin paginación). */
  async linesExport(id: number, filters: CourierSettlementLinesFilters = {}): Promise<{ url_file: string }> {
    const params = buildSettlementLinesQueryParams(filters) as Record<string, unknown>;
    return this.httpClient.get<{ url_file: string }>(
      `${BASE}/${id}/lines/export`,
      params as Record<string, any>,
      { paramsSerializer: serializeSettlementLinesParams },
      true,
    );
  }

  /** Sin cuerpo. Solo líneas por facturar / no facturado (no toca ya facturadas). */
  async recalculatePendingLines(id: number): Promise<CourierSettlementRecalculatePendingResponse> {
    const raw = await this.httpClient.post<unknown>(`${BASE}/${id}/recalculate-pending-lines`, {}, true);
    const r = raw as Record<string, unknown>;
    const payload =
      r.data !== undefined && typeof r.data === 'object' && !Array.isArray(r.data)
        ? (r.data as Record<string, unknown>)
        : r;
    return {
      recalculated: Number(payload.recalculated ?? 0),
      lines_matched_count: Number(payload.lines_matched_count ?? 0),
    };
  }

  /** Respuesta típica Laravel: `{ data: [...] }` o array directo */
  async lines(id: number): Promise<unknown[]> {
    const raw = await this.httpClient.get<unknown>(`${BASE}/${id}/lines`, {}, {}, true);
    if (Array.isArray(raw)) {
      return raw;
    }
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) {
      return r.data as unknown[];
    }
    return [];
  }

  async exceptions(id: number): Promise<unknown[]> {
    const raw = await this.httpClient.get<unknown>(`${BASE}/${id}/exceptions`, {}, {}, true);
    if (Array.isArray(raw)) {
      return raw;
    }
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) {
      return r.data as unknown[];
    }
    return [];
  }
}
